import { api, type Modifications, type Modification } from '../api';
import { callout, field, input, opt, openDialog, select, todayISO, val, numVal } from '../dialog';
import { esc, fmtDate, fmtHours, html, pill, toast } from '../ui';

/**
 * What differs from the type design on this aeroplane.
 *
 * Modifications and repairs together, because they are the same record with a
 * different reason: something was changed under data somebody approved. The
 * screen leads with the approval, since that is the question a review asks
 * first and the one a filed PDF cannot answer.
 */

const KIND_LABEL: Record<string, string> = {
  MODIFICATION: 'Modification',
  REPAIR: 'Repair',
};

export function modificationsCard(d: Modifications): string {
  const live = d.modifications.filter((m) => !m.removedAt);
  const gone = d.modifications.filter((m) => m.removedAt);
  const flagged = live.filter((m) => m.findings.length);

  return html`
    <div class="card">
      <div class="card-h">
        <div>
          <h2>Modifications and repairs</h2>
          <p class="tiny dim">
            Approved changes to the type design — an STC, a major or minor change, a Form 337, a
            repair scheme. ML.A.903 has the review check them; the AD rules read them
          </p>
        </div>
        <button class="btn pri" data-add-modification>Record one</button>
      </div>

      ${flagged.length
        ? callout('caution', '!', html`
            <b>${flagged.length} of ${live.length} need something.</b> M.A.304 asks for approved data,
            and the flight manual supplement, the instructions for continued airworthiness and the
            re-weigh are what a change to the type design drags behind it.
          `)
        : ''}

      ${live.length
        ? html`
            <div class="tblwrap">
              <table class="tbl">
                <thead>
                  <tr>
                    <th>Reference</th><th class="wide">What it is</th>
                    <th>Approved under</th><th>Embodied</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  ${live.map((m) => row(m)).join('')}
                </tbody>
              </table>
            </div>
          `
        : html`<div class="card-b"><p class="muted">
            Nothing recorded. An aircraft with no modifications is perfectly ordinary — but an STC or
            a repair that exists only as a filed PDF is invisible to the AD rules and to the review.
          </p></div>`}

      ${gone.length
        ? html`
            <details class="card-b">
              <summary class="tiny dim">${gone.length} removed — kept, because they were embodied at the time</summary>
              <div class="rows" style="margin-top:8px">
                ${gone.map((m) => html`
                  <div class="row">
                    <div class="bd">
                      <div class="t"><span class="mono">${esc(m.reference)}</span> ${esc(m.title)}</div>
                      <div class="m tiny dim">
                        on ${esc(fmtDate(m.embodiedAt))} · off ${esc(fmtDate(m.removedAt))}
                        ${m.removedReason ? ` — ${esc(m.removedReason)}` : ''}
                      </div>
                    </div>
                  </div>
                `).join('')}
              </div>
            </details>
          `
        : ''}
    </div>
  `;
}

function row(m: Modification): string {
  return html`
    <tr>
      <td class="n"><span class="mono">${esc(m.reference)}</span></td>
      <td class="wide">
        ${esc(m.title)}
        <span class="sub2">
          ${esc(KIND_LABEL[m.kind] ?? m.kind)}${m.location ? ` · ${esc(m.location)}` : ''}
        </span>
        ${m.findings.map((f) => `<span class="sub2" style="color:var(--caution)">${esc(f)}</span>`).join('')}
      </td>
      <td>
        ${esc(m.approvalLabel)}
        ${m.approvalReference
          ? `<span class="sub2 mono">${esc(m.approvalReference)}</span>`
          : '<span class="sub2" style="color:var(--caution)">no reference</span>'}
      </td>
      <td class="tiny">
        ${esc(fmtDate(m.embodiedAt))}
        ${m.embodiedTsn !== null ? `<span class="sub2">${fmtHours(m.embodiedTsn)} h</span>` : ''}
      </td>
      <td style="white-space:nowrap">
        ${m.findings.length ? pill('DUE_SOON', `${m.findings.length}`) : pill('OK', '✓')}
        <button class="btn sm" data-edit-modification="${esc(m.id)}">Edit</button>
        <button class="btn sm" data-remove-modification="${esc(m.id)}">Removed</button>
      </td>
    </tr>
  `;
}

/** The form, shared by recording a new one and correcting an old one. */
function body(d: Modifications, m: Modification | null, kind: string): string {
  const repair = kind === 'REPAIR';
  return html`
    <div class="frow3">
      ${field('What it is', select('kind', d.reference.kinds
        .map((k) => opt(k, KIND_LABEL[k] ?? k, k === kind)).join(''), 'data-kind'))}
      ${field('Reference', input('reference', m?.reference ?? '', 'style="font-family:var(--mono)" maxlength="60"'),
        'What an AD rule will match on')}
      ${field('Embodied', input('embodiedAt', m?.embodiedAt ?? todayISO(), 'type="date"'))}
    </div>
    ${field('What it is called', input('title', m?.title ?? '',
      `maxlength="400" placeholder="${repair ? 'Skin repair, lower fuselage frame 12' : 'Auxiliary fuel tank installation'}"`))}

    <div class="frow3">
      ${field('Approved under', select('approvalBasis', d.reference.bases
        .map((b) => opt(b.basis, b.label, b.basis === (m?.approvalBasis ?? (repair ? 'SRM' : 'EASA_STC')))).join('')))}
      ${field('Approval reference', input('approvalReference', m?.approvalReference ?? '',
        'style="font-family:var(--mono)" maxlength="120"'), 'M.A.304 — the number of the approved data')}
      ${field('Approved', input('approvedAt', m?.approvedAt ?? '', 'type="date"'))}
    </div>
    <div class="frow3">
      ${field('Approval holder', input('approvalHolder', m?.approvalHolder ?? '', 'maxlength="200"'),
        'The design organisation or authority')}
      ${field('Hours at embodiment', input('embodiedTsn', m?.embodiedTsn === null || m === null ? '' : String(m.embodiedTsn),
        'inputmode="decimal" style="text-align:right;font-family:var(--mono)"'), 'Left blank, the aircraft total is taken')}
      ${field('Where on the aircraft', input('location', m?.location ?? '', 'maxlength="200"'),
        repair ? 'Frame, rib, station — what the next repair will be measured from' : 'Optional')}
    </div>

    ${repair
      ? field('The damage', input('damage', m?.damage ?? '', 'maxlength="1000"'),
          'What was wrong, in the words of the assessment')
      : ''}

    <div class="frow3">
      ${field('AFM supplement', input('afmSupplementReference', m?.afmSupplementReference ?? '', 'maxlength="120"'),
        'Where the change needs one')}
      ${field('ICA amended', select('icaAmended', [
        opt('false', 'Not yet', !m?.icaAmended),
        opt('true', 'Yes', Boolean(m?.icaAmended)),
      ].join('')), 'Instructions for continued airworthiness')}
      ${field('Affects weight and balance', select('affectsWeightAndBalance', [
        opt('false', 'No', !m?.affectsWeightAndBalance),
        opt('true', 'Yes — needs a weighing', Boolean(m?.affectsWeightAndBalance)),
      ].join('')))}
    </div>

    ${field('Notes', input('notes', m?.notes ?? '', 'maxlength="1000"'))}

    ${callout('info', 'i', html`
      This is the record the <b>AD rules</b> read when an effectivity says "unless mod X is embodied",
      and the one <b>ML.A.903</b> has an airworthiness review check. The approval paperwork itself
      belongs in the document library — this says what it approves and when it was done.
    `)}
  `;
}

function collect(root: HTMLElement): Record<string, unknown> | { error: string } {
  const reference = val(root, 'reference');
  const title = val(root, 'title');
  if (!reference) return { error: 'A reference is what everything else hangs off — an AD rule matches on it.' };
  if (!title) return { error: 'Say what it is. A reference alone means nothing at a review.' };

  return {
    kind: val(root, 'kind'),
    reference,
    title,
    approvalBasis: val(root, 'approvalBasis'),
    approvalReference: val(root, 'approvalReference') || undefined,
    approvalHolder: val(root, 'approvalHolder') || undefined,
    approvedAt: val(root, 'approvedAt') || undefined,
    embodiedAt: val(root, 'embodiedAt'),
    embodiedTsn: numVal(root, 'embodiedTsn'),
    location: val(root, 'location') || undefined,
    damage: val(root, 'damage') || undefined,
    afmSupplementReference: val(root, 'afmSupplementReference') || undefined,
    icaAmended: val(root, 'icaAmended') === 'true',
    affectsWeightAndBalance: val(root, 'affectsWeightAndBalance') === 'true',
    notes: val(root, 'notes') || undefined,
  };
}

export function bindModifications(root: HTMLElement, aircraftId: string, d: Modifications, reload: () => void): void {
  root.querySelector('[data-add-modification]')?.addEventListener('click', () => {
    let kind = 'MODIFICATION';
    openDialog<Record<string, unknown>>({
      wide: true,
      title: 'Record a modification or repair',
      sub: 'What differs from the type design, and under whose approval',
      primary: 'Record it',
      body: () => body(d, null, kind),
      onRender: (form, refresh) => {
        form.querySelector<HTMLSelectElement>('[data-kind]')?.addEventListener('change', (e) => {
          // A repair asks two questions a modification does not — where, and
          // what was wrong — so the form follows the answer.
          kind = (e.target as HTMLSelectElement).value;
          refresh();
        });
      },
      collect,
      submit: (b) => api.modifications.record(aircraftId, b),
      done: (r: Modifications) => `${r.modifications.filter((m) => !m.removedAt).length} on the aircraft`,
      onDone: reload,
    });
  });

  root.querySelectorAll<HTMLElement>('[data-edit-modification]').forEach((b) =>
    b.addEventListener('click', () => {
      const m = d.modifications.find((x) => x.id === b.dataset.editModification);
      if (!m) return;
      let kind = m.kind;
      openDialog<Record<string, unknown>>({
        wide: true,
        title: 'Correct the record',
        sub: `${m.reference} — ${m.title.slice(0, 60)}`,
        primary: 'Save it',
        body: () => body(d, m, kind),
        onRender: (form, refresh) => {
          form.querySelector<HTMLSelectElement>('[data-kind]')?.addEventListener('change', (e) => {
            kind = (e.target as HTMLSelectElement).value;
            refresh();
          });
        },
        collect,
        submit: (patch) => api.modifications.update(aircraftId, m.id, patch),
        done: () => 'Saved',
        onDone: reload,
      });
    }),
  );

  root.querySelectorAll<HTMLElement>('[data-remove-modification]').forEach((b) =>
    b.addEventListener('click', () => {
      const m = d.modifications.find((x) => x.id === b.dataset.removeModification);
      if (!m) return;
      openDialog<Record<string, unknown>>({
        title: 'Taken off the aircraft',
        sub: `${m.reference} — ${m.title.slice(0, 60)}`,
        primary: 'Record the removal',
        body: () => html`
          <div class="frow">
            ${field('When', input('removedAt', todayISO(), 'type="date"'))}
            ${field('Why', input('reason', '', 'maxlength="400" placeholder="Reverted to the standard installation"'))}
          </div>
          ${callout('info', 'i', html`
            The record stays. It was embodied for all the time it was on, and an AD assessed while it
            was fitted was right at the time — deleting it would make the aircraft's history disagree
            with the assessments filed against it.
          `)}
        `,
        collect: (form) => {
          const reason = val(form, 'reason');
          if (!reason) return { error: 'Say why it came off.' };
          return { reason, removedAt: val(form, 'removedAt') || undefined };
        },
        submit: (bd) => api.modifications.remove(aircraftId, m.id, bd),
        done: () => 'Recorded as removed',
        onDone: reload,
      });
    }),
  );

  // Nothing here refuses on a finding — they are for the reviewer to weigh.
  root.querySelectorAll<HTMLElement>('[data-mod-finding]').forEach((el) =>
    el.addEventListener('click', () => toast(el.dataset.modFinding ?? '')),
  );
}
